import { EventEmitter, Injectable, RendererFactory2 } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { NotificationCRT } from './crt-notification-handler';

/**
 * Enum du type de communication qui déclanche le Toast
 */
export enum CrtTypeCommToast {
  /**
   * Communication complete
   */
  COMMUNICATION_COMPLETE = 'COMMUNICATION_COMPLETE',
  /**
   * Communication Light
   */
  COMMUNICATION_LIGHT = 'COMMUNICATION_LIGHT',
  /**
   * Communcation flash sur un PH
   */
  COMMUNICATION_FLASH_PH = 'COMMUNICATION_FLASH_PH',
  /**
   * Communcation flash sur une LR
   */
  COMMUNICATION_FLASH_LR = 'COMMUNICATION_FLASH_LR',
}

/**
 * Type d'event à la fermeture d'un toast
 */
export enum CrtToastCloseType {
  /**
   * Fermeture automatique du toast
   * Remplacement d'un toast
   */
  AUTO_CLOSE = 'auto-close',
  /**
   * Click sur toast
   * @type {string}
   */
  ACTION = 'action',
  /**
   * Click sur le bouton fermé du toast
   * @type {string}
   */
  CLOSE = 'close',
}

/**
 * Class CSS suivant le type de Toast
 */
enum CrtToastTypeClass {
  /**
   * Class CSS toast en sucse
   */
  SUCCESS = 'crt-toast-success',
  /**
   * Class CSS toast en warning
   * @type {string}
   */
  WARNING = 'crt-toast-warning',
  /**
   * Class CSS toast en erreur
   * @type {string}
   */
  DANGER = 'crt-toast-danger',
  /**
   * Class CSS toast d'information
   * @type {string}
   */
  INFO = 'crt-toast-info',
}

/**
 * Option d'un Toast
 */
export interface CrtToastOption {
  /**
   * Message du toast
   */
  message: string;
  /**
   * Effectuer une action a la fermeture du toast
   */
  action: boolean;
  /**
   * Données du toast
   */
  data?: NotificationCRT;
  /**
   * Class CSS du toast
   */
  cssClass?: string;
  /**
   * Si le toast à une durée d'affichage
   */
  isTimed?: boolean;
  /**
   * Durée d'affichage du toast. Si null, prend la valeur par défaut de 3s
   */
  duration?: number;
}

/**
 * Reponse d'un evenement
 */
export interface CrtToastEventResponse {
  /**
   * Donnée du toast
   */
  data: any;
  /**
   * Type de toast
   */
  type: string;
}

/**
 * Crt Toast Controller
 * @example
 * fromPromise(
 this.crtToast.createSuccess({
        message: 'test msg',
        action: true,
        data: {
          siteId: 1234
        }
      })
 )
 .pipe(switchMap(evt => evt))
 .subscribe((crtToastResp: CrtToastEventResponse) => {
        console.log(crtToastResp.data, crtToastResp.type);
        if (crtToastResp.type === CrtToastCloseType.ACTION) {
          // do action
        }
      });
 */
@Injectable({
  providedIn: 'root',
})
export class CrtToastController {
  /**
   * Toast en cours
   */
  private currentToast: {
    opts: CrtToastOption;
    toast?: HTMLIonToastElement;
    evt: EventEmitter<CrtToastEventResponse>;
  };

  /**
   * Durée en ms par défaut du toast si activé
   */
  private defaultDuration = 3000;

  /**
   * Constructeur
   * @param {ToastController} ionToastCtrl
   * @param {RendererFactory2} rendererFactory
   */
  constructor(
    private ionToastCtrl: ToastController,
    private rendererFactory: RendererFactory2
  ) {}

  /**
   * Creation d'un toast de succes
   * @param {CrtToastOption} opts
   * @returns {Promise<EventEmitter<CrtToastEventResponse>>}
   */
  public async createSuccess(opts: CrtToastOption): Promise<EventEmitter<CrtToastEventResponse>> {
    return await this.create({
      ...opts,
      cssClass: `${CrtToastTypeClass.SUCCESS} ${opts.cssClass}`,
    });
  }

  /**
   * Creation d'un toast d'erreur
   * @param {CrtToastOption} opts
   * @returns {Promise<EventEmitter<CrtToastEventResponse>>}
   */
  public async createDanger(opts: CrtToastOption): Promise<EventEmitter<CrtToastEventResponse>> {
    return await this.create({
      ...opts,
      cssClass: `${CrtToastTypeClass.DANGER} ${opts.cssClass}`,
    });
  }

  /**
   * Creation d'un toast de dangé
   * @param {CrtToastOption} opts
   * @returns {Promise<EventEmitter<CrtToastEventResponse>>}
   */
  public async createWarning(opts: CrtToastOption): Promise<EventEmitter<CrtToastEventResponse>> {
    return await this.create({
      ...opts,
      cssClass: `${CrtToastTypeClass.WARNING} ${opts.cssClass}`,
    });
  }

  /**
   * Creation d'un toast d'info
   * @param {CrtToastOption} opts
   * @returns {Promise<EventEmitter<CrtToastEventResponse>>}
   */
  public async createInfo(opts: CrtToastOption): Promise<EventEmitter<CrtToastEventResponse>> {
    return await this.create({
      ...opts,
      cssClass: `${CrtToastTypeClass.INFO} ${opts.cssClass}`,
    });
  }

  /**
   * Creation d'un toast
   * Gestion du clique et de la fermeture
   * @param {CrtToastOption} opts
   * @returns {Promise<EventEmitter<CrtToastEventResponse>>}
   */
  private async create(opts: CrtToastOption): Promise<EventEmitter<CrtToastEventResponse>> {
    // Si un Toast est déjà présent on le ferme
    if (this.currentToast && this.currentToast.toast) {
      await this.currentToast.toast.dismiss(null, CrtToastCloseType.AUTO_CLOSE);
    }

    this.currentToast = {
      opts: opts,
      evt: new EventEmitter<any>(),
    };

    this.currentToast.toast = await this.ionToastCtrl.create({
      duration: opts.isTimed ? opts.duration || this.defaultDuration : undefined,
      message: opts.message,
      cssClass: 'crt-toast ' + opts.cssClass,
      position: 'bottom',
      buttons: [
        {
          icon: 'close',
          role: 'cancel',
          handler: () => {
            this.currentToast.evt.emit({
              data: this.currentToast.opts.data,
              type: CrtToastCloseType.CLOSE,
            });
            this.currentToast.toast.dismiss();
          },
        },
      ],
    });

    await this.currentToast.toast.present().then(() => this.toastClickListener());

    return this.currentToast.evt;
  }

  /**
   * Ecouteur de clique sur le toast
   */
  private toastClickListener() {
    // Creation d'un renderer a partir de l'element natif
    const renderer = this.rendererFactory.createRenderer(this.currentToast.toast.shadowRoot, null);
    renderer.listen(this.currentToast.toast.shadowRoot, 'click', (clickEvt) => {
      if (this.currentToast.opts.action && clickEvt.target.className === 'toast-message') {
        this.currentToast.evt.emit({
          data: this.currentToast.opts.data,
          type: CrtToastCloseType.ACTION,
        });
        this.currentToast.toast.dismiss();
      }
    });
  }
}
